import { Dispatch, SetStateAction, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNotification } from '@topcon/topcon-utils';
import {
  TeBadge,
  TeButton,
  TeButtonOutline,
  TeContainer,
  TeContainerSlim,
  TeDivider,
  TeIcon,
  TeText,
} from '@topcon/tuicl-react';

import { CombinedUser, Status } from '../types';
import ProfileSelector from '../../../components/ProfileSelector';
import {
  useUpdateInviteToJoin,
  useUpdateUser,
  useCreateUser,
  useDeleteUser,
} from '../userManagement.mutations';
import { groupByFocusIndustry } from '../../../utils/utils';
import { useInvitationDetails, useUserDetails } from '../userManagement.queries';
import { useAppService } from '../../../providers/app/app.context';
import UserConfirmModal from './UserConfirmModal';
import { IdentityBody, ProfileBody } from '../../../api/generated/pap';
import { resolveProfileDisplayName } from '../../../utils/profiles';
import ProfileImage from '../../../components/ProfileImage';
import { useErrorDrawer } from '../../../providers/ErrorDrawerContext/error-drawer.context';

interface Props {
  user: CombinedUser | undefined;
  onClose: (shouldUpdate: boolean) => void;
  editMode: boolean;
  setEditMode: Dispatch<SetStateAction<boolean>>;
  isInvitation: boolean;
  userConfirmModalOpen: boolean;
  setUserConfirmModalOpen: Dispatch<SetStateAction<boolean>>;
  currentLoggedUser: IdentityBody | undefined;
}

function UserProfile({
  user,
  onClose,
  editMode,
  setEditMode,
  isInvitation,
  userConfirmModalOpen,
  setUserConfirmModalOpen,
  currentLoggedUser,
}: Props) {
  const { t } = useTranslation('users');
  const [originalProfiles] = useState<string[]>(user?.profileIds || []);
  const [profiles, setProfiles] = useState<string[]>(user?.profileIds || []);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { mutateAsync: updateInviteToJoin } = useUpdateInviteToJoin();
  const { mutateAsync: updateUser } = useUpdateUser();
  const { mutateAsync: createUser } = useCreateUser();
  const { mutateAsync: deleteUser } = useDeleteUser();
  const notificationService = useNotification();
  const { showError } = useErrorDrawer();
  const { availableProfiles, isOwner: isCurrentUserOwner } = useAppService();
  const currentProfiles = availableProfiles.filter((profile) =>
    profiles.includes(profile.profileId)
  );
  const groupedProfiles = groupByFocusIndustry(currentProfiles) as Record<string, ProfileBody[]>;

  useUserDetails(!isInvitation && user ? user.id : undefined);
  useInvitationDetails(isInvitation && user ? user.id : undefined);

  // Find administrator profile ID from available profiles
  const administratorProfileId = availableProfiles.find((profile) =>
    profile.profileId.includes('administrator')
  )?.profileId;

  // Check if user is the owner
  const isOwner = user?.isOwner || false;
  // Check if owner has a user record (id is not just identityId)
  const ownerHasUserRecord = isOwner && user?.id && user.identityId && user.id !== user.identityId;
  // Check if profiles contain only administrator (or empty)
  const hasOnlyAdministrator =
    !profiles.length ||
    (profiles.length === 1 && administratorProfileId && profiles.includes(administratorProfileId));

  const updateProfiles = async () => {
    if (user) {
      setIsSubmitting(true);
      try {
        if (isInvitation) {
          await updateInviteToJoin({
            id: user.id,
            payload: { profileIds: profiles },
          });
        } else if (isOwner) {
          // Owner-specific logic
          if (!ownerHasUserRecord && !hasOnlyAdministrator) {
            // Owner wants to add profiles beyond administrator -> POST to create user
            // profiles should include all selected profiles (including administrator if selected)
            if (!user.identityId) {
              throw new Error('Owner identityId is required');
            }
            await createUser({
              identityId: user.identityId,
              profileIds: profiles,
            });
          } else if (ownerHasUserRecord && hasOnlyAdministrator) {
            // Owner wants to remove all profiles except administrator -> DELETE user
            // Virtual administrator profile will remain
            await deleteUser(user.id);
          } else if (ownerHasUserRecord) {
            // Owner has user record and updating profiles -> PUT
            if (!user.identityId) {
              throw new Error('Owner identityId is required');
            }
            await updateUser({
              id: user.id,
              payload: {
                profileIds: profiles,
                identityId: user.identityId,
              },
            });
          }
          // If owner doesn't have user record and has only administrator, no action needed
          // (virtual administrator profile is sufficient)
        } else if (user.identityId) {
          // Regular user update
          await updateUser({
            id: user.id,
            payload: {
              profileIds: profiles,
              identityId: user.identityId,
            },
          });
        }
        setEditMode(false);
        onClose(true);
      } catch (error: unknown) {
        console.error('Error updating user profiles', error);
        if (showError(error)) {
          return;
        }
        notificationService.addNotification(
          t('profileUpdateFailed'),
          t('profileUpdateFailedDescription'),
          'error'
        );
      } finally {
        setIsSubmitting(false);
        setUserConfirmModalOpen(false);
      }
    }
  };

  const canEditProfiles = () => {
    if (isCurrentUserOwner) {
      return true;
    }
    if (user?.isOwner) {
      return false;
    }
    return user?.identityId !== currentLoggedUser?.id;
  };

  const renderProfileInfo = () => {
    return (
      <TeContainer padding={['l', 'm']} column fill>
        <TeContainerSlim justifyContent="flex-end" fill>
          <TeButton
            size="s"
            icon="close"
            teclick={() => {
              onClose(false);
            }}
            variant="neutral"
          />
        </TeContainerSlim>
        <TeContainerSlim height={96} width={96} justifyContent="center" alignItems="center">
          <ProfileImage email={user?.email ?? ''} size={96} />
        </TeContainerSlim>
        <TeContainerSlim column fill gap="xs">
          <TeText size={20} weight={700}>
            {user?.name}
          </TeText>
          {user?.status !== Status.Invited && <TeText color="text.secondary">{user?.email}</TeText>}
          {user?.status === Status.Invited && (
            <TeBadge
              size="s"
              text={t('invited')}
              backgroundColor="background.info.secondary"
              textColor="text.primary"
            />
          )}
        </TeContainerSlim>
      </TeContainer>
    );
  };

  const renderProfileList = () => {
    return (
      <TeContainer column fill gap="xs2" overflow="auto">
        <TeContainerSlim fill alignItems="center" justifyContent="space-between" height="m">
          <TeText weight={700}>{t('profiles')}</TeText>
          {canEditProfiles() && (
            <TeButtonOutline
              size="s"
              text={t('edit')}
              icon="edit"
              teclick={() => setEditMode(true)}
            />
          )}
        </TeContainerSlim>

        <TeContainerSlim column fill gap="m">
          {Object.keys(groupedProfiles).map((focusIndustry) => {
            return (
              <TeContainerSlim column fill gap="xs" key={focusIndustry}>
                <TeText size={12} color="text.secondary">
                  {/* TODO: Translations for focus industries */}
                  {t(`${focusIndustry}.details.title`, { ns: 'profile' }) ?? focusIndustry}
                </TeText>
                <TeDivider dividerColor="border.secondary" />
                <TeContainerSlim column fill gap="xs">
                  {groupedProfiles[focusIndustry].map((profile) => (
                    <TeContainer
                      key={profile.profileId}
                      fill
                      backgroundColor="background.tertiary"
                      borderRadius={4}
                      alignItems="center"
                      justifyContent="space-between"
                      padding={[0, 'm']}
                      height={40}
                    >
                      <TeText weight={700}>{resolveProfileDisplayName(profile, t)}</TeText>
                    </TeContainer>
                  ))}
                </TeContainerSlim>
              </TeContainerSlim>
            );
          })}
        </TeContainerSlim>
      </TeContainer>
    );
  };

  const renderAssignProfiles = () => {
    return (
      <>
        <TeContainerSlim grow column gap="xs" overflow="auto">
          <TeContainer column grow gap="xs2">
            <TeContainerSlim fill alignItems="center" justifyContent="space-between">
              <TeContainerSlim gap="xs" alignItems="center" height="m">
                <TeText weight={700}>{t('editProfiles')}</TeText>
              </TeContainerSlim>
              <TeContainerSlim gap="xs2" alignItems="center">
                <TeIcon color="icon.secondary" name="info" />
                <TeText size={12} color="text.secondary">
                  {t('oneProfileRequired')}
                </TeText>
              </TeContainerSlim>
            </TeContainerSlim>
            <ProfileSelector profiles={profiles} setProfiles={setProfiles} />
          </TeContainer>

          <TeContainer fill gap="xs" padding="xs" shadow="top">
            <TeButton
              size="s"
              variant="neutral"
              text={t('cancel')}
              grow
              teclick={() => {
                setProfiles(originalProfiles);
                setEditMode(false);
              }}
            />
            <TeButton
              size="s"
              text={t('saveChanges')}
              grow
              teclick={() => {
                setUserConfirmModalOpen(true);
              }}
              disabled={!profiles.length || profiles === originalProfiles}
            />
          </TeContainer>
        </TeContainerSlim>
        <UserConfirmModal
          user={user}
          profiles={profiles}
          open={userConfirmModalOpen}
          loading={isSubmitting}
          onClose={() => setUserConfirmModalOpen(false)}
          onSubmit={updateProfiles}
        />
      </>
    );
  };

  return (
    <>
      {renderProfileInfo()}
      <TeDivider dividerColor="border.secondary" />
      {editMode ? renderAssignProfiles() : renderProfileList()}
    </>
  );
}

export default UserProfile;
